import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';

import { ColumnViewDef } from '../../view';
import { OptionDTO } from '../../dtos';
import { doRun, FilterDelayDefault, isAssigned, isEmpty } from '../../utils';
import { DateRangeComponent } from '../date-range/date-range.component';

@Component({
    standalone: true,
    imports: [CommonModule, FormsModule, InputTextModule, InputNumberModule, SelectModule, TooltipModule, DateRangeComponent],
    selector: 'idb-data-table-filter',
    templateUrl: './data-table-filter.component.html',
    styleUrls: ['./data-table-filter.component.scss'],
})
export class DataTableFilterComponent {
    private _timeout: any = null;
    private _column: ColumnViewDef = { name: '', header: '' };
    private _hasDelay = false;
    private _withFilterDelay = false;
    private _filterDelay = FilterDelayDefault;
    private _filter: any = undefined;
    private _pendingShortFilter = false;

    constructor() {}

    private checlHasDelay() {
        this._hasDelay =
            this._withFilterDelay &&
            this._filterDelay > 0 &&
            isAssigned(this._column) &&
            isAssigned(this._column.isFilter) &&
            (this._column.isFilter === 0 || this._column.isFilter === 1);
    }

    @Input()
    filterByLabel = 'Filter by';

    @Input()
    readonly = false;

    @Input()
    disabled = false;

    @Input()
    set withFilterDelay(value: boolean) {
        this._withFilterDelay = value;
        this.checlHasDelay();
    }

    @Input()
    set filterDelay(value: number) {
        this._filterDelay = value;
        this.checlHasDelay();
    }

    @Input()
    set column(value: ColumnViewDef) {
        this._column = value;
        this.checlHasDelay();
    }
    get column(): ColumnViewDef {
        return this._column;
    }

    @Input()
    set filter(value: any) {
        if (this._pendingShortFilter && isAssigned(value)) {
            return;
        }

        this._pendingShortFilter = false;
        this._filter = value;
    }
    get filter(): any {
        return this._filter;
    }

    @Output()
    filterChange = new EventEmitter<any>();

    get filterOptions(): OptionDTO[] {
        if (isAssigned(this.column) && this.column.isFilter === 2 && isAssigned(this.column.filterOptions)) return this.column.filterOptions!() ?? [];

        return [];
    }

    get filterShowSearch(): boolean {
        if (isAssigned(this.column) && this.column.isFilter === 2 && isAssigned(this.column.filterSearchOnMoreThan))
            return this.filterOptions.length > this.column.filterSearchOnMoreThan!;

        return false;
    }

    get filterByLabelInternal(): string {
        if (isAssigned(this.column.filterPlaceholder)) {
            return this.column.filterPlaceholder!();
        }

        const header = this.column.displayHeader ? this.column.displayHeader!() : (this._column.header ?? this._column.name);

        return `${this.filterByLabel} ${header}`;
    }

    get filterTooltipInternal(): string {
        if (isAssigned(this.column.filterTooltip)) {
            return this.column.filterTooltip!();
        }

        return this.filterByLabelInternal;
    }

    get filterControlWidth(): string {
        return `${Math.ceil(this.filterByLabelInternal.length * 0.5 + 4.5)}rem`;
    }

    get filterInputStyle(): { width: string; maxWidth: string } {
        return { width: '100%', maxWidth: this.filterControlWidth };
    }

    isAssigned(value: any | undefined): boolean {
        return isAssigned(value);
    }

    private meetsFilterMinLength(value: any): boolean {
        const minLength = this.column.filterMinLength;
        if (!isAssigned(minLength) || minLength! <= 0) {
            return true;
        }

        if (isEmpty(value)) {
            return true;
        }

        return typeof value === 'string' && value.trim().length >= minLength!;
    }

    private emitFilter(value: any): void {
        this._pendingShortFilter = false;

        if (this._hasDelay) {
            this._timeout = doRun(() => this.filterChange.emit(value), this._filterDelay, this._timeout);
        } else {
            this.filterChange.emit(value);
        }
    }

    onFilterClear($e: any) {
        this._filter = undefined;
        this._pendingShortFilter = false;

        if (this._hasDelay) {
            clearTimeout(this._timeout);
        }

        this.filterChange.emit(this._filter);
    }

    onFilterChange($e: any) {
        this._filter = $e;

        if (!this.meetsFilterMinLength($e)) {
            this._pendingShortFilter = true;

            if (this._hasDelay) {
                clearTimeout(this._timeout);
            }

            return;
        }

        this.emitFilter($e);
    }

    onEnter($e: any) {
        if (this.disabled || this.readonly) {
            return;
        }

        $e.stopPropagation();

        if (this._hasDelay) {
            clearTimeout(this._timeout);
        }

        if (!this.meetsFilterMinLength(this._filter)) {
            this._pendingShortFilter = true;
            return;
        }

        this._pendingShortFilter = false;
        this.filterChange.emit(this._filter);
    }

    onKeypress($e: KeyboardEvent) {
        if (this.column.isFilter === 0 && isAssigned(this.column.isValidKey) && !this.column.isValidKey!($e.key)) {
            $e.preventDefault();
        }
    }
}
